'use client';
/**
 * Lot N5 — PLAN DU LABORATOIRE, section du paramétrage.
 *
 * ── Ce que cet écran est, et ce qu'il n'est pas ────────────────────────────
 *
 * Ce n'est PAS un éditeur graphique de plan. Dessiner des murs et y poser des
 * icônes est un chantier à part entière, et ce n'est pas lui qui rend service :
 * ce qui rend service, c'est de RETROUVER un objet. Les trois questions que
 * cet écran doit savoir répondre, et qui ont dicté sa forme :
 *
 *   · « où est la centrifugeuse ? »        → la recherche, qui cherche aussi
 *                                            DANS le contenu des emplacements ;
 *   · « qu'y a-t-il dans l'armoire A2 ? »  → le panneau d'un emplacement ;
 *   · « quels documents sont affichés en   → le même panneau, colonne des
 *      biochimie ? »                         copies maîtrisées.
 *
 * La troisième est celle qu'un auditeur pose en circulant dans le laboratoire.
 * C'est elle qui justifie que les copies maîtrisées, les équipements et les
 * lignes de stockage partagent UN SEUL référentiel d'endroits au lieu de trois
 * colonnes de texte libre.
 *
 * ── Ce que l'écran refuse de faire ────────────────────────────────────────
 *
 *  · MASQUER LES TROUS. « 237 équipements sans emplacement » s'affiche en
 *    haut, en toutes lettres, avec le moyen d'aller les renseigner. Le jour de
 *    la livraison, c'est l'information la plus utile de cet écran — et la
 *    cacher derrière un onglet donnerait un plan qui a l'air complet.
 *
 *  · DEVINER UN RATTACHEMENT. Aucun bouton « rapprocher automatiquement » :
 *    « Salle biochimie » et « Biochimie » se ressemblent, mais décider qu'elles
 *    désignent le même endroit est une décision du laboratoire. Un
 *    rattachement faux ne se voit pas ; une case vide, si.
 *
 *  · SUPPRIMER EN SILENCE. Un emplacement occupé ne se supprime pas d'un clic :
 *    le serveur refuse en nommant ce qui s'y trouve, et il faut dire où les
 *    objets vont — ailleurs, ou nulle part, mais explicitement.
 *
 * ── Le stockage se tient ICI (13/10/2026) ─────────────────────────────────
 *
 * Le paramétrage montrait deux onglets, « Plan du laboratoire » et « Lieux de
 * stockage », pour une seule réalité. Le second faisait taper zone, armoire et
 * étagère en texte libre : une ligne créée là n'était rattachée à aucun
 * endroit, et n'apparaissait pas ici. Retour d'Ines : « cela ne peut pas être
 * le plan, avec un niveau de détail permettant d'arriver au lieu de
 * stockage ? ». On descend donc jusqu'à l'étagère, et c'est sur elle qu'on
 * ajoute, corrige ou archive ce qui y est rangé. L'onglet a disparu ; le
 * registre (ORG.K2.01) et ses 43 lignes n'ont pas bougé.
 */
import React from 'react';
import { ficheRenseignee, ficheEffleuree, piecesSansFiche, phraseFiches } from '@/lib/fiches-locaux';
import { refusDepot } from '@/lib/depot';
import {
  I, card, inputStyle, EmptyState, Skeleton, useBusy, RefISO, ConfirmButton, ChampRecherche, Modale} from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
import { BoutonJournal } from '@/components/BoutonJournal';
import { ActionsLigne, ArchiverModale, FormulaireLigne } from '@/components/RegistreEdition';
import type { ChampDef } from '@/components/RegistreEdition';
import { styleBouton } from '@/shared/boutonStyles';
/* ── Ce que le moteur sert ─────────────────────────────────────────────── */

type Objet = {
  documentId: string; ref: string; titre: string;
  detail: string; complement: string; statut: string;
  /** Lignes de stockage seulement : ce que le formulaire de correction relit. */
  remarque?: string; emplacementCode?: string; archiveMotif?: string;
};
type Compte = Record<string, number>;
/** Les rubriques du §6.3, portées par une PIÈCE. */
export interface FicheLocal {
  usage: string;
  conditionsRequises: string;
  /** ⚠️ « aucune » est un FAIT, pas un vide : au 27/08/2026 aucune salle du
   *  laboratoire n'est surveillée. Les enceintes le sont en tant qu'ÉQUIPEMENTS
   *  (§6.4) — c'est l'ambiance des pièces qui n'est pas mesurée. */
  surveillance: 'aucune' | 'releve' | 'enregistreur';
  separationDe: string;
  accesRestreint: string;
  risques: string;
}

const LOCAL_VIDE = (): FicheLocal => ({
  usage: '', conditionsRequises: '', surveillance: 'aucune',
  separationDe: '', accesRestreint: '', risques: '',
});

const SURVEILLANCE_LABEL: Record<string, string> = {
  aucune: 'Aucune surveillance',
  releve: 'Relevé manuel',
  enregistreur: 'Enregistreur continu',
};

type Noeud = {
  documentId: string; code: string; libelle: string;
  niveau: string; niveauLibelle: string; parentCode: string;
  description: string; temperature: string; remarque: string;
  /** Fiche de local (§6.3) — présente uniquement sur une pièce. */
  local: FicheLocal | null;
  ordre: number; actif: boolean;
  plan: { name: string; ext: string; cle: string; size: number; legende?: string } | null;
  source: string;
  chemin: string[]; enfants: number;
  contenu: Record<string, Objet[]>;
  compte: Compte; cumule: Compte;
};
type Plan = {
  niveaux: { cle: string; libelle: string }[];
  emplacements: Noeud[];
  sansEmplacement: Compte;
  totaux: Compte;
  registres: { cle: string; libelle: string; pluriel: string }[];
  orphelins: { code: string; compte: Compte }[];
  /** Lignes de stockage archivées : hors du plan, rétablissables. */
  archivees: Objet[];
  peutEcrire: boolean;
};

/** Ce qu'on saisit d'une ligne de stockage. L'ENDROIT n'y est pas : c'est
 *  l'étagère ouverte, et le moteur en déduit zone, armoire et étagère. */
const CHAMPS_STOCKAGE: readonly ChampDef[] = [
  { k: 'contenu', label: 'Contenu', requis: true, type: 'long', large: true },
  { k: 'temperature', label: 'Température de conservation', exemple: 'ex. 2–8 °C' },
  { k: 'remarque', label: 'Remarque', type: 'long', large: true },
];

const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5 };

async function moteur(chemin: string, corps?: unknown): Promise<{ ok: boolean; data: unknown; erreur: string }> {
  const r = await fetch(`/api/ged/${chemin}`, {
    method: corps === undefined ? 'GET' : 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: corps === undefined ? undefined : JSON.stringify(corps),
  }).catch(() => null);
  if (!r) return { ok: false, data: null, erreur: 'Le serveur n’a pas répondu.' };
  const j = await r.json().catch(() => null);
  if (!r.ok) {
    // Le message du serveur, mot pour mot : c'est lui qui nomme ce qui bloque
    // (« porte encore 3 équipements »). Le remplacer par « échec » obligerait
    // à aller chercher soi-même.
    const m = (j as { error?: { message?: string } })?.error?.message || `Erreur ${r.status}`;
    return { ok: false, data: null, erreur: m };
  }
  return { ok: true, data: j, erreur: '' };
}

/** Compte lisible d'un registre. Un zéro s'écrit « — » : « 0 équipement » et
 *  « aucun équipement renseigné ici » ne veulent pas dire la même chose, et
 *  c'est l'arbre entier qui serait tapissé de zéros sans intérêt. */
const Pastille = ({ n, titre, couleur }: { n: number; titre: string; couleur: string }) => (
  n > 0
    ? (
      <span title={titre} style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5, fontWeight: 700,
        color: couleur, background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-pill)', padding: '1px 7px',
      }}>{n}</span>
    )
    : null
);

const COULEURS: Record<string, string> = {
  equipements: 'var(--color-primary)',
  stockage: 'var(--amber-600)',
  copies: 'var(--navy-900)',
};

/* ── L'arbre ───────────────────────────────────────────────────────────── */

function Branche({
  noeud, enfantsDe, profondeur, choisi, onChoisir, ouverts, onBasculer, registres, filtre,
}: {
  noeud: Noeud; enfantsDe: Map<string, Noeud[]>; profondeur: number;
  choisi: string; onChoisir: (c: string) => void;
  ouverts: Set<string>; onBasculer: (c: string) => void;
  registres: Plan['registres']; filtre: Set<string> | null;
}) {
  const enfants = enfantsDe.get(noeud.code) || [];
  // Sous filtre, un nœud reste affiché s'il correspond OU s'il porte un
  // descendant qui correspond : sinon la recherche « centrifugeuse » ferait
  // disparaître la zone qui la contient, et le résultat serait orphelin.
  if (filtre && !filtre.has(noeud.code)) return null;
  const ouvert = filtre ? true : ouverts.has(noeud.code);
  const actif = choisi === noeud.code;

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '5px 8px', paddingInlineStart: 8 + profondeur * 16,
        background: actif ? 'var(--cyan-50)' : 'transparent',
        borderRadius: 'var(--radius-sm)', cursor: 'pointer',
      }}>
        <button
          onClick={(e) => { e.stopPropagation(); onBasculer(noeud.code); }}
          disabled={!enfants.length || !!filtre}
          aria-label={ouvert ? 'Replier' : 'Déplier'}
          style={{
            flex: 'none', width: 18, height: 18, display: 'grid', placeItems: 'center',
            background: 'transparent', border: 'none', padding: 0,
            color: enfants.length ? 'var(--text-muted)' : 'transparent',
            cursor: enfants.length && !filtre ? 'pointer' : 'default',
          }}>
          {!!enfants.length && <I n={ouvert ? 'chevron-bas' : 'chevron-right'} s={13} />}
        </button>
        <button
          onClick={() => onChoisir(noeud.code)}
          style={{
            flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, textAlign: 'start',
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
          }}>
          <span style={{
            fontSize: 13, fontWeight: actif ? 700 : 500,
            color: noeud.actif ? (actif ? 'var(--color-primary)' : 'var(--text-body)') : 'var(--text-muted)',
            textDecoration: noeud.actif ? 'none' : 'line-through',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{noeud.libelle}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-muted)', flex: 'none' }}>{noeud.code}</span>
          <span style={{ display: 'inline-flex', gap: 4, marginInlineStart: 'auto', flex: 'none' }}>
            {registres.map((r) => (
              <Pastille key={r.cle} n={noeud.cumule[r.cle] || 0}
                titre={`${noeud.cumule[r.cle]} ${noeud.cumule[r.cle] > 1 ? r.pluriel : r.libelle}, ici ou en dessous`}
                couleur={COULEURS[r.cle] || 'var(--text-muted)'} />
            ))}
          </span>
        </button>
      </div>
      {ouvert && enfants.map((e) => (
        <Branche key={e.code} noeud={e} enfantsDe={enfantsDe} profondeur={profondeur + 1}
          choisi={choisi} onChoisir={onChoisir} ouverts={ouverts} onBasculer={onBasculer}
          registres={registres} filtre={filtre} />
      ))}
    </div>
  );
}

/* ── L'écran ───────────────────────────────────────────────────────────── */

export function PlanLabo() {
  const [plan, setPlan] = React.useState<Plan | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [q, setQ] = React.useState('');
  const [choisi, setChoisi] = React.useState('');
  const [ouverts, setOuverts] = React.useState<Set<string>>(new Set());
  const [edition, setEdition] = React.useState<Partial<Noeud> & { nouveau?: boolean } | null>(null);
  /** L'endroit tel qu'il était à l'ouverture de sa modification (14/10/2026). */
  const baseEdition = React.useRef<Partial<Noeud> | null>(null);
  const [trous, setTrous] = React.useState<{ registre: string; libelle: string; pluriel: string; lignes: Objet[] } | null>(null);
  /** Une ligne de stockage en saisie : `ligne` nulle = création sur `code`. */
  const [saisieStock, setSaisieStock] = React.useState<{ ligne: Objet | null; code: string } | null>(null);
  const [archivageStock, setArchivageStock] = React.useState<Objet | null>(null);
  const [voirArchivees, setVoirArchivees] = React.useState(false);
  const { busy, run } = useBusy();

  /** Pose l'état à partir d'une réponse du moteur.
   *
   *  Écrit deux fois — dans l'effet de montage et dans `charger` — au lieu
   *  d'être appelé depuis l'effet : `react-hooks/set-state-in-effect` est une
   *  ERREUR de lint dans ce module, et il suit les fonctions locales. Les
   *  `setState` doivent donc se trouver LEXICALEMENT dans une continuation
   *  asynchrone, comme partout ailleurs dans ces écrans. */
  const charger = React.useCallback(async () => {
    const r = await moteur('ged/emplacements');
    if (!r.ok) { setErreur(r.erreur); return; }
    setErreur('');
    const p = r.data as Plan;
    setPlan(p);
    // Les racines s'ouvrent seules : un arbre entièrement replié à
    // l'ouverture donne l'impression d'un référentiel vide.
    setOuverts((o) => (o.size ? o : new Set(p.emplacements.filter((e) => !e.parentCode).map((e) => e.code))));
  }, []);

  React.useEffect(() => {
    let vivant = true;
    void moteur('ged/emplacements').then((r) => {
      if (!vivant) return;
      if (!r.ok) { setErreur(r.erreur); return; }
      const p = r.data as Plan;
      setErreur('');
      setPlan(p);
      setOuverts(new Set(p.emplacements.filter((e) => !e.parentCode).map((e) => e.code)));
    });
    return () => { vivant = false; };
  }, []);

  const index = React.useMemo(
    () => new Map((plan?.emplacements || []).map((e) => [e.code, e])),
    [plan],
  );
  const enfantsDe = React.useMemo(() => {
    const m = new Map<string, Noeud[]>();
    for (const e of plan?.emplacements || []) {
      if (!e.parentCode || !index.has(e.parentCode)) continue;
      const l = m.get(e.parentCode) || [];
      l.push(e);
      m.set(e.parentCode, l);
    }
    for (const l of m.values()) l.sort((a, b) => a.ordre - b.ordre || a.code.localeCompare(b.code));
    return m;
  }, [plan, index]);
  const racines = React.useMemo(
    () => (plan?.emplacements || [])
      .filter((e) => !e.parentCode || !index.has(e.parentCode))
      .sort((a, b) => a.ordre - b.ordre || a.code.localeCompare(b.code)),
    [plan, index],
  );

  /** Filtre : un emplacement passe s'il correspond LUI-MÊME, ou si l'un des
   *  objets qu'il porte correspond — c'est ce qui fait répondre « où est la
   *  centrifugeuse ? ». Ses ancêtres passent avec lui, sans quoi le résultat
   *  apparaîtrait détaché de l'arbre. */
  const filtre = React.useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t || !plan) return null;
    const gardes = new Set<string>();
    for (const e of plan.emplacements) {
      const propre = [e.code, e.libelle, e.description, e.remarque, e.temperature, ...e.chemin]
        .some((v) => String(v || '').toLowerCase().includes(t));
      const dedans = Object.values(e.contenu).some((liste) => liste.some(
        (o) => [o.ref, o.titre, o.detail, o.complement].some((v) => String(v || '').toLowerCase().includes(t)),
      ));
      if (!propre && !dedans) continue;
      let courant: Noeud | undefined = e;
      for (let garde = 0; courant && garde <= plan.emplacements.length; garde += 1) {
        gardes.add(courant.code);
        courant = courant.parentCode ? index.get(courant.parentCode) : undefined;
      }
    }
    return gardes;
  }, [q, plan, index]);

  const noeud = choisi ? index.get(choisi) || null : null;

  const basculer = (c: string) => setOuverts((o) => {
    const n = new Set(o);
    if (n.has(c)) n.delete(c); else n.add(c);
    return n;
  });

  const enregistrer = () => run('save', async () => {
    if (!edition) return;
    const corpsDe = (x: Partial<Noeud>): Record<string, unknown> => ({
      code: x.code, libelle: x.libelle, niveau: x.niveau,
      parentCode: x.parentCode ?? '', description: x.description ?? '',
      temperature: x.temperature ?? '', remarque: x.remarque ?? '',
      /* La fiche n'est envoyée QUE pour une pièce : le moteur refuse ailleurs,
         et l'envoyer quand même ferait échouer l'enregistrement d'une étagère
         sur une rubrique qui ne la concerne pas. */
      ...(x.niveau === 'piece' ? { local: x.local ?? LOCAL_VIDE() } : {}),
      ordre: x.ordre ?? 0, actif: x.actif !== false,
    });
    const corps = corpsDe(edition);
    /* Ce que l'écran a lu à l'ouverture de la modification (14/10/2026) : le
       moteur n'écrit que ce qui a changé depuis, et refuse (409) ce qu'un
       autre a changé entre-temps. */
    const lu = baseEdition.current;
    if (!edition.nouveau && lu && lu.documentId === edition.documentId) corps.base = corpsDe(lu);
    const cible = edition.nouveau ? 'ged/emplacements' : `ged/emplacements/${edition.documentId}`;
    const r = await moteur(cible, corps);
    if (!r.ok) { setErreur(r.erreur); return; }
    setErreur('');
    const code = (r.data as { code?: string })?.code || '';
    setEdition(null);
    if (code) setChoisi(code);
    await charger();
  });

  const supprimer = (versCode: string, detacher: boolean) => run('del', async () => {
    if (!noeud) return;
    const r = await moteur(`ged/emplacements/${noeud.documentId}/supprimer`,
      versCode ? { versCode } : { detacher });
    if (!r.ok) { setErreur(r.erreur); return; }
    setErreur('');
    setChoisi(noeud.parentCode || '');
    await charger();
  });

  const rattacher = (registre: string, documentId: string, emplacementCode: string) => run(`att-${documentId}`, async () => {
    const r = await moteur('ged/emplacements/rattacher', { registre, documentId, emplacementCode });
    if (!r.ok) { setErreur(r.erreur); return; }
    setErreur('');
    await charger();
    if (trous) await ouvrirTrous(trous.registre);
  });

  const ouvrirTrous = React.useCallback(async (registre: string) => {
    const r = await moteur(`ged/emplacements/sans-emplacement/${registre}`);
    if (!r.ok) { setErreur(r.erreur); return; }
    const d = r.data as { registre: string; libelle: string; pluriel: string; lignes: Objet[] };
    setTrous({ registre: d.registre, libelle: d.libelle, pluriel: d.pluriel, lignes: d.lignes });
  }, []);

  const joindrePlan = (fichier: File) => run('plan', async () => {
    if (!noeud) return;
    // Poids et contenu réel avant l'envoi (SEC-10 b) : le message nomme le
    // poids du fichier et la limite. Le serveur revalide (`verifierBinaire`).
    const refus = await refusDepot('technique', fichier, 'Le plan');
    if (refus) { setErreur(refus); return; }
    const b64 = await new Promise<string>((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(String(fr.result).split(',')[1] || '');
      fr.onerror = () => rej(new Error('lecture'));
      fr.readAsDataURL(fichier);
    });
    const r = await moteur(`ged/emplacements/${noeud.documentId}/plan`, { name: fichier.name, base64: b64 });
    if (!r.ok) { setErreur(r.erreur); return; }
    setErreur('');
    await charger();
  });

  if (!plan) return <Skeleton rows={5} />;
  const registres = plan.registres;

  return (
    <div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '0 0 14px', lineHeight: 1.55 }}>
        Le référentiel des endroits du laboratoire — <strong>un seul</strong>, partagé par les
        équipements, les lignes de stockage et les copies maîtrisées affichées. Il sert à
        <em> retrouver</em> : où est un appareil, ce que contient une armoire, quels documents
        sont affichés dans une pièce. Descendez jusqu’à l’étagère pour y ajouter ou corriger ce
        qui y est rangé — l’endroit, pas la quantité, qui reste dans Boxystock. <RefISO code="6.3" />
      </p>

      {erreur && (
        <div role="alert" style={{
          display: 'flex', gap: 9, alignItems: 'flex-start', padding: '10px 13px', marginBottom: 12,
          background: 'var(--amber-50)', border: '1px solid var(--amber-300)',
          borderRadius: 'var(--radius-md)', fontSize: 12.5, color: 'var(--text-strong)',
        }}>
          <I n="alert" s={15} /><span>{erreur}</span>
        </div>
      )}

      {/* ⚠️ LES FICHES §6.3 MANQUANTES SE COMPTENT EN HAUT, PAS DANS CHAQUE
          PIÈCE (MAN-2). L'avertissement n'existait qu'À L'INTÉRIEUR d'un nœud :
          il fallait ouvrir les dix-neuf pièces une à une pour savoir combien
          restaient à décrire, et l'on repartait en croyant que c'était fait.
          C'est le même défaut que celui relevé partout ailleurs dans l'audit —
          une obligation visible seulement de l'endroit où on l'a déjà remplie.
          Les pièces sont NOMMÉES et cliquables : un compte sans les noms
          obligerait à rouvrir les dix-neuf pour retrouver lesquelles. */}
      {(() => {
        const sansFiche = piecesSansFiche(plan.emplacements);
        if (!sansFiche.length) return null;
        return (
          <div style={{
            padding: '11px 14px', marginBottom: 14,
            background: 'var(--amber-50)', border: '1px solid var(--amber-300)',
            borderRadius: 'var(--radius-md)', fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-strong)',
          }}>
            <strong>{phraseFiches(plan.emplacements)}</strong>{' '}
            <span style={{ color: 'var(--text-muted)' }}>
              §6.3 attend, pour chaque pièce, à quoi elle sert.
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
              {sansFiche.map((n) => (
                <button key={n.code} type="button"
                  onClick={() => setOuverts((o) => new Set([...o, ...(n.chemin || []), n.code]))}
                  className="ged-btn"
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, borderRadius: 'var(--radius-pill)', padding: '2px 9px', border: '1px solid var(--amber-300)', background: 'var(--surface-card)', color: 'var(--amber-600)', cursor: 'pointer' }}>
                  {n.code}
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Ce qui n'est rattaché nulle part. En haut, en toutes lettres. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {registres.map((r) => {
          const sans = plan.sansEmplacement[r.cle] || 0;
          const total = plan.totaux[r.cle] || 0;
          return (
            <button key={r.cle} onClick={() => void ouvrirTrous(r.cle)} className="ged-btn"
              style={{
                ...styleBouton('contour', 'sm'), textAlign: 'start', flexDirection: 'column', alignItems: 'flex-start',
                gap: 2, padding: '8px 12px', borderColor: sans ? 'var(--amber-300)' : undefined,
              }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: sans ? 'var(--amber-600)' : 'var(--text-strong)' }}>
                {sans === 0 ? `Tous les ${r.pluriel} sont situés` : `${sans} ${sans > 1 ? r.pluriel : r.libelle} sans emplacement`}
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-muted)' }}>
                sur {total} · cliquer pour les renseigner
              </span>
            </button>
          );
        })}
      </div>

      {(plan.peutEcrire || !!plan.archivees.length) && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
          {!!plan.archivees.length && (
            <Bouton taille="sm" variante="discret" onClick={() => setVoirArchivees(true)}>
              <I n="archive" s={13} />{plan.archivees.length} ligne{plan.archivees.length > 1 ? 's' : ''} de stockage archivée{plan.archivees.length > 1 ? 's' : ''}
            </Bouton>
          )}
          <BoutonJournal reference="REG-STOCK-LIEUX" heritage="ORG.K2.01" quoi="des lieux de stockage" />
        </div>
      )}

      {!!plan.orphelins.length && (
        <div style={{
          padding: '9px 12px', marginBottom: 12, fontSize: 12.5,
          background: 'var(--surface-sunken)', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)', color: 'var(--text-body)',
        }}>
          <strong>{plan.orphelins.length} code(s) d’emplacement cité(s) mais absent(s) du référentiel</strong>{' '}
          — {plan.orphelins.map((o) => o.code).join(', ')}. Les objets concernés ne sont pas perdus :
          recréez le code, ou rattachez-les ailleurs.
        </div>
      )}

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Arbre */}
        <div style={{ ...card, padding: 0, flex: '1 1 300px', minWidth: 280, overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid var(--border-default)' }}>
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Où est… ? (appareil, contenu, document)"
              style={{ ...inputStyle, height: 32, fontSize: 12.5, flex: 1, width: 'auto' }} />
            {plan.peutEcrire && (
              <Bouton taille="sm" title="Ajouter un emplacement racine" onClick={() => setEdition({ nouveau: true, code: '', libelle: '', niveau: 'zone', parentCode: '', actif: true, ordre: 0 })} style={{ flex: 'none' }}>
                <I n="plus" s={13} />
              </Bouton>
            )}
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto', padding: '8px 6px' }}>
            {!plan.emplacements.length && (
              <EmptyState titre="Aucun emplacement n’est défini."
                indice="Le référentiel part des endroits déjà écrits au plan des entrepôts ; ajoutez les autres au fur et à mesure." />
            )}
            {!!plan.emplacements.length && filtre && !filtre.size && (
              <EmptyState titre={`Rien ne correspond à « ${q} ».`}
                indice="La recherche porte sur les emplacements et sur ce qu’ils contiennent." />
            )}
            {racines.map((r) => (
              <Branche key={r.code} noeud={r} enfantsDe={enfantsDe} profondeur={0}
                choisi={choisi} onChoisir={setChoisi} ouverts={ouverts} onBasculer={basculer}
                registres={registres} filtre={filtre} />
            ))}
          </div>
        </div>

        {/* Panneau de l'emplacement choisi */}
        <div style={{ ...card, flex: '1 1 340px', minWidth: 300 }}>
          {!noeud && (
            <EmptyState titre="Choisissez un emplacement."
              indice="Son contenu s’affiche ici : appareils, lignes de stockage, documents affichés." />
          )}
          {noeud && (
            <div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, justifyContent: 'space-between' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 3 }}>
                    {noeud.chemin.slice(0, -1).join(' › ') || noeud.niveauLibelle}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <I n="pin" s={16} />
                    <strong style={{ fontSize: 15, color: 'var(--text-strong)' }}>{noeud.libelle}</strong>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>{noeud.code}</span>
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>· {noeud.niveauLibelle}</span>
                    {!noeud.actif && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)' }}>hors service</span>}
                  </div>
                  {noeud.niveau === 'piece' && (
                    <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', display: 'grid', gap: 5 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)' }}>
                        Fiche de local · ISO 15189 §6.3
                      </div>
                      {/* ⚠️ « Aucune surveillance » s'affiche EN CLAIR, comme
                          une réponse. Laisser la ligne vide se lirait « pas
                          encore renseigné », et l'on attendrait une saisie qui
                          ne viendra pas : au 27/08/2026 aucune salle n'est
                          surveillée, et c'est un fait déclaré. */}
                      <div style={{ fontSize: 12.5 }}>
                        Surveillance de l’ambiance : <strong>{SURVEILLANCE_LABEL[noeud.local?.surveillance || 'aucune']}</strong>
                        {(noeud.local?.surveillance || 'aucune') === 'aucune' && (
                          <span style={{ color: 'var(--text-muted)' }}> — les enceintes sont suivies comme équipements (§6.4)</span>
                        )}
                      </div>
                      {[['usage', 'Usage'], ['conditionsRequises', 'Conditions requises'],
                        ['separationDe', 'Séparé de'], ['accesRestreint', 'Accès'], ['risques', 'Risques']]
                        .map(([k, lib]) => {
                          const v = (noeud.local as unknown as Record<string, string> | null)?.[k];
                          return v ? (
                            <div key={k} style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                              <span style={{ color: 'var(--text-muted)' }}>{lib} : </span>{v}
                            </div>
                          ) : null;
                        })}
                      {/* ⚠️ L'AVERTISSEMENT PORTE SUR LE CONTENU, PLUS SUR
                          L'EXISTENCE DE L'OBJET. Ouvrir l'éditeur d'une pièce et
                          enregistrer suffisait à créer une fiche vide, et
                          l'avertissement disparaissait : il ne restait
                          qu'« Aucune surveillance », qui se lit comme une
                          réponse complète. Une fiche devenait remplie parce
                          qu'on l'avait effleurée — un état vide affirmant
                          « ce local a été décrit » (même famille que BLOQ-03). */}
                      {!ficheRenseignee(noeud.local) && (
                        <div style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>
                          {ficheEffleuree(noeud.local)
                            ? 'Fiche commencée mais vide — §6.3 attend au moins l’usage de la pièce.'
                            : 'Fiche non renseignée — §6.3 attend au moins l’usage de la pièce et ce qui y est séparé.'}
                        </div>
                      )}
                    </div>
                  )}
                  {!!noeud.temperature && (
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>Température : {noeud.temperature}</div>
                  )}
                  {!!noeud.description && (
                    <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 6, whiteSpace: 'pre-wrap' }}>{noeud.description}</div>
                  )}
                </div>
                {plan.peutEcrire && (
                  <div style={{ display: 'flex', gap: 6, flex: 'none' }}>
                    <Bouton taille="sm" title="Modifier" onClick={() => { baseEdition.current = { ...noeud }; setEdition({ ...noeud }); }}><I n="pen" s={13} /></Bouton>
                    <Bouton taille="sm" title="Ajouter un sous-emplacement" onClick={() => setEdition({
                        nouveau: true, code: `${noeud.code}-`, libelle: '', parentCode: noeud.code,
                        niveau: plan.niveaux[Math.min(plan.niveaux.findIndex((n) => n.cle === noeud.niveau) + 1, plan.niveaux.length - 1)]?.cle || 'position',
                        actif: true, ordre: (enfantsDe.get(noeud.code) || []).length,
                      })}><I n="plus" s={13} /></Bouton>
                  </div>
                )}
              </div>

              {/* Image du plan — jointe, pas dessinée. */}
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-default)' }}>
                <div style={{ ...lbl, marginBottom: 7 }}>Image du plan</div>
                {noeud.plan
                  ? (
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <a href={`/api/ged/ged/equip/fichiers/${noeud.plan.cle}/${noeud.plan.ext}`}
                        target="_blank" rel="noreferrer"
                        style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary)' }}>
                        <I n="eye" s={13} /> {noeud.plan.name}
                      </a>
                      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{Math.round(noeud.plan.size / 1024)} Ko</span>
                      {plan.peutEcrire && (
                        <ConfirmButton label="" titre="Supprimer l’image" icon="trash" variant="danger"
                          question="Supprimer l’image du plan ? Elle ne se récupère pas." confirmer="Supprimer"
                          onConfirm={() => run('planoff', async () => {
                            const r = await moteur(`ged/emplacements/${noeud.documentId}/plan`, { retirer: true });
                            if (!r.ok) { setErreur(r.erreur); return; }
                            await charger();
                          })} />
                      )}
                    </div>
                  )
                  : (
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                      Aucune image jointe.{' '}
                      {plan.peutEcrire && (
                        <label style={{ color: 'var(--color-primary)', fontWeight: 600, cursor: 'pointer' }}>
                          Joindre un plan (PNG, JPEG ou PDF)
                          <input type="file" accept=".png,.jpg,.jpeg,.pdf" style={{ display: 'none' }}
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) joindrePlan(f); e.target.value = ''; }} />
                        </label>
                      )}
                    </div>
                  )}
                {busy === 'plan' && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>Envoi…</div>}
              </div>

              {/* Ce qui se trouve ICI */}
              {registres.map((r) => {
                const liste = noeud.contenu[r.cle] || [];
                const sous = (noeud.cumule[r.cle] || 0) - liste.length;
                return (
                  <div key={r.cle} style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-default)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                      <div style={{ ...lbl, marginBottom: 0 }}>
                        {r.pluriel} ici {liste.length ? `(${liste.length})` : ''}
                        {sous > 0 && <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}> · {sous} en dessous</span>}
                      </div>
                      {r.cle === 'stockage' && plan.peutEcrire && (
                        <Bouton taille="sm" onClick={() => setSaisieStock({ ligne: null, code: noeud.code })}
                          aria-label={`Ajouter un contenu à ${noeud.chemin.join(' › ')}`} style={{ marginInlineStart: 'auto' }}>
                          <I n="plus" s={13} />Ajouter un contenu
                        </Bouton>
                      )}
                    </div>
                    {!liste.length && (
                      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                        Aucun{r.cle === 'copies' ? 'e' : ''} {r.libelle} n’est rattaché{r.cle === 'copies' ? 'e' : ''} à cet emplacement.
                      </div>
                    )}
                    <div style={{ display: 'grid', gap: 5 }}>
                      {liste.map((o) => (
                        <div key={o.documentId} style={{
                          display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
                          padding: '6px 9px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)',
                        }}>
                          {!!o.ref && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--navy-900)' }}>{o.ref}</span>}
                          <span style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>{o.titre}</span>
                          {!!o.detail && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{o.detail}</span>}
                          {o.statut === 'retiree' && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)' }}>retirée</span>}
                          {plan.peutEcrire && r.cle === 'stockage' && (
                            /* Une ligne de stockage se CORRIGE ou s'ARCHIVE d'ici —
                               jamais « détachée » : sans endroit, elle ne dirait
                               plus où la chose est rangée. */
                            <span style={{ marginInlineStart: 'auto' }}>
                              <ActionsLigne onCorriger={() => setSaisieStock({ ligne: o, code: noeud.code })}
                                onArchiver={() => setArchivageStock(o)} />
                            </span>
                          )}
                          {plan.peutEcrire && r.cle !== 'stockage' && (
                            <Bouton taille="sm" disabled={busy === `att-${o.documentId}`} onClick={() => rattacher(r.cle, o.documentId, '')} style={{ marginInlineStart: 'auto' }}>Détacher</Bouton>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {plan.peutEcrire && (
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-default)' }}>
                  <SuppressionGardee noeud={noeud} emplacements={plan.emplacements}
                    occupe={registres.reduce((n, r) => n + (noeud.compte[r.cle] || 0), 0)}
                    onSupprimer={supprimer} busy={busy === 'del'} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {edition && (
        <Formulaire valeur={edition} niveaux={plan.niveaux} emplacements={plan.emplacements}
          onChange={setEdition} onAnnuler={() => { setEdition(null); setErreur(''); }}
          onEnregistrer={enregistrer} busy={busy === 'save'} />
      )}

      {saisieStock && (
        <FormulaireLigne chemin="ged/stock/lieux"
          titreCreation={`Ajouter un contenu — ${index.get(saisieStock.code)?.chemin.join(' › ') || saisieStock.code}`}
          titreCorrection="Corriger la ligne de stockage"
          champs={saisieStock.ligne ? [
            /* En correction seulement : changer d'étagère sans archiver ni recréer. */
            { k: 'emplacementCode', label: 'Rangé à', type: 'options', large: true,
              options: plan.emplacements
                .filter((e) => e.actif || e.code === saisieStock.ligne?.emplacementCode)
                .map((e) => [e.code, e.chemin.join(' › ')] as const) },
            ...CHAMPS_STOCKAGE,
          ] : CHAMPS_STOCKAGE}
          ligne={saisieStock.ligne ? {
            documentId: saisieStock.ligne.documentId, contenu: saisieStock.ligne.titre,
            temperature: saisieStock.ligne.complement, remarque: saisieStock.ligne.remarque || '',
            emplacementCode: saisieStock.ligne.emplacementCode || saisieStock.code,
          } : null}
          complementPayload={saisieStock.ligne ? undefined : () => ({ emplacementCode: saisieStock.code })}
          onClose={() => setSaisieStock(null)} onDone={() => { void charger(); }} />
      )}

      {archivageStock && (
        <ArchiverModale chemin="ged/stock/lieux"
          ligne={{ documentId: archivageStock.documentId, archive: archivageStock.statut === 'archivee', archiveMotif: archivageStock.archiveMotif || null }}
          refLigne={`${archivageStock.ref} — ${archivageStock.titre}`}
          onClose={() => setArchivageStock(null)} onDone={() => { void charger(); }} />
      )}

      {voirArchivees && (
        <Modale largeur={720} onClose={() => setVoirArchivees(false)}
          titre="Lignes de stockage archivées"
          sousTitre="Sorties du plan, mais pas effacées : elles restent citables par une non-conformité ou un audit, et se rétablissent d’ici.">
          <div style={{ display: 'grid', gap: 6 }}>
            {!plan.archivees.length && <EmptyState titre="Aucune ligne archivée." />}
            {plan.archivees.map((o) => (
              <div key={o.documentId} style={{
                display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
                padding: '8px 11px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)',
              }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--navy-900)', flex: 'none' }}>
                  {(o.emplacementCode && index.get(o.emplacementCode)?.chemin.join(' › ')) || o.ref}
                </span>
                <span style={{ fontSize: 12.5, color: 'var(--text-strong)', flex: '1 1 160px', minWidth: 0 }}>{o.titre}</span>
                {!!o.archiveMotif && <span style={{ fontSize: 12.5, fontStyle: 'italic', color: 'var(--text-muted)' }}>« {o.archiveMotif} »</span>}
                {plan.peutEcrire && (
                  <Bouton taille="sm" onClick={() => { setVoirArchivees(false); setArchivageStock(o); }}>
                    <I n="refresh" s={13} />Rétablir
                  </Bouton>
                )}
              </div>
            ))}
          </div>
        </Modale>
      )}

      {trous && (
        <Trous trous={trous} emplacements={plan.emplacements} peutEcrire={plan.peutEcrire}
          onFermer={() => setTrous(null)} onRattacher={rattacher} busy={busy} />
      )}
    </div>
  );
}

/* ── Suppression : jamais silencieuse ──────────────────────────────────── */

function SuppressionGardee({
  noeud, emplacements, occupe, onSupprimer, busy,
}: {
  noeud: Noeud; emplacements: Noeud[]; occupe: number;
  onSupprimer: (versCode: string, detacher: boolean) => void; busy: boolean;
}) {
  const [vers, setVers] = React.useState('');
  const autres = emplacements.filter((e) => e.code !== noeud.code);

  if (noeud.enfants > 0) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        Cet emplacement contient {noeud.enfants} sous-emplacement(s) : il ne peut pas être supprimé
        tant qu’ils y pendent.
      </div>
    );
  }
  if (!occupe) {
    return (
      <ConfirmButton label="Supprimer cet emplacement" icon="trash" variant="danger"
        question="Supprimer cet emplacement ? Il ne porte aucun objet."
        disabled={busy} onConfirm={() => onSupprimer('', false)} />
    );
  }
  return (
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginBottom: 8 }}>
        <strong>{occupe} objet(s)</strong> se trouvent ici. Supprimer l’emplacement ne les efface pas,
        mais il faut dire où ils vont — sinon leur rattachement disparaîtrait sans que personne
        l’ait décidé.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={vers} onChange={(e) => setVers(e.target.value)}
          style={{ ...inputStyle, height: 32, fontSize: 12.5, width: 'auto', minWidth: 220 }}>
          <option value="">Les détacher (à réaffecter plus tard)</option>
          {autres.map((e) => (
            <option key={e.code} value={e.code}>Les déplacer vers {e.chemin.join(' › ')}</option>
          ))}
        </select>
        <ConfirmButton label="Supprimer" icon="trash" variant="danger"
          question={vers ? `Déplacer les ${occupe} objet(s) vers ${vers}, puis supprimer ?` : `Détacher les ${occupe} objet(s), puis supprimer ?`}
          disabled={busy} onConfirm={() => onSupprimer(vers, !vers)} />
      </div>
    </div>
  );
}

/* ── Formulaire de création / modification ─────────────────────────────── */

function Formulaire({
  valeur, niveaux, emplacements, onChange, onAnnuler, onEnregistrer, busy,
}: {
  valeur: Partial<Noeud> & { nouveau?: boolean };
  niveaux: Plan['niveaux']; emplacements: Noeud[];
  onChange: (v: Partial<Noeud> & { nouveau?: boolean }) => void;
  onAnnuler: () => void; onEnregistrer: () => void; busy: boolean;
}) {
  const maj = (k: string, v: unknown) => onChange({ ...valeur, [k]: v });
  // Un emplacement ne peut pas pendre de lui-même ni de l'un de ses
  // descendants. Le serveur le refuse ; la liste ne les propose pas, pour que
  // le refus n'ait pas à se produire.
  const interdits = React.useMemo(() => {
    const bloc = new Set<string>();
    if (!valeur.code) return bloc;
    const marquer = (c: string) => {
      if (bloc.has(c)) return;
      bloc.add(c);
      for (const e of emplacements) if (e.parentCode === c) marquer(e.code);
    };
    marquer(String(valeur.code));
    return bloc;
  }, [valeur.code, emplacements]);

  return (
    /* Le composant `Modale` de la maison, et non plus une boîte refaite à la
       main (24/08/2026) : Échap ferme, la croix est en haut, et le voile se
       ferme au `mousedown` — commencer une sélection de texte dans la boîte et
       relâcher la souris dehors ne l'escamote plus avec la saisie en cours. */
    <Modale largeur={520} onClose={onAnnuler}
      titre={valeur.nouveau ? 'Nouvel emplacement' : `Modifier ${valeur.libelle}`}
      sousTitre={<>Le code est recopié dans les fiches d’équipement et les copies maîtrisées, et se tape à la
        main : majuscules non accentuées, chiffres, « - » et « _ ».</>}
      pied={<>
        <Bouton taille="sm" onClick={onAnnuler}>Annuler</Bouton>
        <Bouton variante="primaire" onClick={onEnregistrer} disabled={busy || !valeur.code || !valeur.libelle}>
          <I n="check" s={14} />{busy ? 'Enregistrement…' : 'Enregistrer'}
        </Bouton>
      </>}>
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={lbl} htmlFor="emp-code">Code</label>
              <input id="emp-code" value={valeur.code ?? ''} style={inputStyle}
                onChange={(e) => maj('code', e.target.value.toUpperCase())} />
            </div>
            <div>
              <label style={lbl} htmlFor="emp-niv">Niveau</label>
              <select id="emp-niv" value={valeur.niveau ?? 'position'} style={inputStyle}
                onChange={(e) => maj('niveau', e.target.value)}>
                {niveaux.map((n) => <option key={n.cle} value={n.cle}>{n.libelle}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={lbl} htmlFor="emp-lib">Intitulé</label>
            <input id="emp-lib" value={valeur.libelle ?? ''} style={inputStyle}
              onChange={(e) => maj('libelle', e.target.value)} />
          </div>
          <div>
            <label style={lbl} htmlFor="emp-par">Se trouve dans</label>
            <select id="emp-par" value={valeur.parentCode ?? ''} style={inputStyle}
              onChange={(e) => maj('parentCode', e.target.value)}>
              <option value="">— racine du plan —</option>
              {emplacements.filter((e) => !interdits.has(e.code)).map((e) => (
                <option key={e.code} value={e.code}>{e.chemin.join(' › ')}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={lbl} htmlFor="emp-temp">Température de conservation</label>
              <input id="emp-temp" value={valeur.temperature ?? ''} style={inputStyle}
                placeholder="ex. 2–8 °C" onChange={(e) => maj('temperature', e.target.value)} />
            </div>
            <div>
              <label style={lbl} htmlFor="emp-ordre">Ordre d’affichage</label>
              <input id="emp-ordre" type="number" value={valeur.ordre ?? 0} style={inputStyle}
                onChange={(e) => maj('ordre', Number(e.target.value))} />
            </div>
          </div>
          <div>
            {/* ── FICHE DE LOCAL (§6.3) — SUR UNE PIÈCE SEULEMENT ──────────
                Une étagère n'a ni activités incompatibles ni contrôle d'accès :
                proposer ces rubriques partout remplirait le référentiel de
                champs vides qu'on cesserait de lire. */}
            {valeur.niveau === 'piece' && (
              <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: 12, display: 'grid', gap: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)' }}>
                  Fiche de local · ISO 15189 §6.3
                </div>
                <div>
                  <label style={lbl} htmlFor="loc-surv">Surveillance de l’ambiance</label>
                  <select id="loc-surv" style={inputStyle}
                    value={valeur.local?.surveillance || 'aucune'}
                    onChange={(e) => maj('local', { ...(valeur.local || LOCAL_VIDE()), surveillance: e.target.value })}>
                    <option value="aucune">Aucune surveillance</option>
                    <option value="releve">Relevé manuel</option>
                    <option value="enregistreur">Enregistreur continu</option>
                  </select>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
                    Il s’agit de l’AMBIANCE de la pièce. Les réfrigérateurs, congélateurs et étuves sont
                    suivis à part, comme équipements (§6.4).
                  </div>
                </div>
                {([['usage', 'Usage — ce qu’on y fait', 'ex. paillasse de biochimie, automates et centrifugeuse'],
                   ['conditionsRequises', 'Conditions requises', 'Laisser vide tant que rien n’est exigé — une plage inventée deviendrait un écart le jour où on la mesure'],
                   ['separationDe', 'Séparé de — activités incompatibles', 'ex. extraction séparée de l’amplification'],
                   ['accesRestreint', 'Accès', 'ex. libre, ou réservé aux techniciens habilités'],
                   ['risques', 'Risques', 'ex. produits chimiques, agents biologiques']] as const)
                  .map(([k, libelle, aide]) => (
                    <div key={k}>
                      <label style={lbl} htmlFor={`loc-${k}`}>{libelle}</label>
                      <input id={`loc-${k}`} style={inputStyle}
                        value={(valeur.local as unknown as Record<string, string> | undefined)?.[k] || ''}
                        onChange={(e) => maj('local', { ...(valeur.local || LOCAL_VIDE()), [k]: e.target.value })} />
                      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{aide}</div>
                    </div>
                  ))}
              </div>
            )}
            <label style={lbl} htmlFor="emp-desc">Description</label>
            <textarea id="emp-desc" value={valeur.description ?? ''} rows={2}
              style={{ ...inputStyle, height: 'auto', resize: 'vertical' }}
              onChange={(e) => maj('description', e.target.value)} />
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--text-body)' }}>
            <input type="checkbox" checked={valeur.actif !== false} onChange={(e) => maj('actif', e.target.checked)} />
            Emplacement en service. Le décocher le garde au référentiel sans le proposer aux
            nouveaux rattachements — ceux qui existent ne bougent pas.
          </label>
        </div>
    </Modale>
  );
}

/* ── Les objets sans emplacement ───────────────────────────────────────── */

function Trous({
  trous, emplacements, peutEcrire, onFermer, onRattacher, busy,
}: {
  trous: { registre: string; libelle: string; pluriel: string; lignes: Objet[] };
  emplacements: Noeud[]; peutEcrire: boolean; onFermer: () => void;
  onRattacher: (registre: string, documentId: string, code: string) => void;
  busy: string | null;
}) {
  const [q, setQ] = React.useState('');
  const vues = React.useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return trous.lignes;
    return trous.lignes.filter((o) => [o.ref, o.titre, o.detail, o.complement]
      .some((v) => String(v || '').toLowerCase().includes(t)));
  }, [trous.lignes, q]);
  const proposables = emplacements.filter((e) => e.actif);

  return (
    /* `Modale` de la maison (24/08/2026) : Échap ferme, et le voile réagit au
       `mousedown` — une sélection de texte relâchée hors de la boîte ne la
       referme plus. La recherche reste dans l'EN-TÊTE, qui ne défile pas :
       c'est le contrat du composant, corps seul défilant. */
    <Modale largeur={780} onClose={onFermer}
      titre={`${trous.lignes.length} ${trous.lignes.length > 1 ? trous.pluriel : trous.libelle} sans emplacement`}
      sousTitre={<>
        Rien n’est deviné ici : aucun rapprochement automatique n’est proposé, parce qu’un
        rattachement faux ne se voit pas, alors qu’une case vide se voit. Chaque ligne se
        renseigne à la main, par celui qui sait où l’objet se trouve.
        <ChampRecherche valeur={q} onChange={setQ} placeholder="Rechercher…"
          style={{ height: 32, fontSize: 12.5, marginTop: 10, maxWidth: 'none' }} />
      </>}>
        <div>
          {!vues.length && <EmptyState titre="Rien à renseigner ici." />}
          <div style={{ display: 'grid', gap: 6 }}>
            {vues.map((o) => (
              <div key={o.documentId} style={{
                display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
                padding: '8px 11px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)',
              }}>
                {!!o.ref && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--navy-900)', flex: 'none' }}>{o.ref}</span>}
                <span style={{ fontSize: 12.5, color: 'var(--text-strong)', flex: '1 1 160px', minWidth: 0 }}>{o.titre}</span>
                {/* Le texte libre d'origine reste sous les yeux : c'est lui qui
                    aide à choisir le bon endroit, et c'est la seule chose que
                    l'outil sache dire de la position de cet objet. */}
                {!!o.detail && <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: 'none' }}>{o.detail}</span>}
                {!!o.complement && <span style={{ fontSize: 12.5, fontStyle: 'italic', color: 'var(--text-muted)', flex: 'none' }}>« {o.complement} »</span>}
                {peutEcrire && (
                  <select defaultValue="" disabled={busy === `att-${o.documentId}`}
                    onChange={(e) => { if (e.target.value) onRattacher(trous.registre, o.documentId, e.target.value); }}
                    style={{ ...inputStyle, height: 30, fontSize: 12.5, width: 'auto', minWidth: 190, flex: 'none' }}>
                    <option value="">Situer…</option>
                    {proposables.map((e) => (
                      <option key={e.code} value={e.code}>{e.chemin.join(' › ')}</option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
    </Modale>
  );
}
